import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardTileTypeEnum } from '@modules/api/dashboard-tile-type-enum.generated';
import { DashboardViewModel } from '@modules/api/dashboard-view-model.generated';
import { GetDashboardTileCountsService } from '@modules/api/get-dashboard-tile-counts.service.generated';
import { OESubheaderViewModel } from '@modules/api/oe-subheader-view-model.generated';
import { ConcurrentRequestHandlingEnum } from '@modules/common/enums/concurrent-request-handling-enum';
import { ChangeDetectorWrapper } from '@modules/common/models/change-detector-wrapper';
import { CurrentCaseService } from '@modules/common/services/current-case.service';
import { CurrentEnvironmentService } from '@modules/common/services/current-environment.service';
import { GetCurrentUserDynamicMenusService } from '@modules/common/services/get-current-user-dynamic-menus.service';
import { ExtendedDashboardTileViewModel } from '@modules/dashboard/models/extended-dashboard-tile-view-model';
import { Title } from '@angular/platform-browser';
import * as moment from 'moment';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ChangeDetectorWrapper]
})
export class DashboardComponent implements OnInit, OnDestroy {
    public tiles: ExtendedDashboardTileViewModel[] = [];
    public DashboardTileTypeEnum = DashboardTileTypeEnum;
    public lastUpdate?: Date;
    public oeSubheader?: ExtendedOESubheaderViewModel;
    todaysDate = new Date();

    /** The tile (if any) that is currently displaying the details overlay. */
    public currentDetailTile?: ExtendedDashboardTileViewModel;

    private _getDashboardsSubscription?: Subscription;

    constructor(
        private readonly _getCurrentUserDynamicMenusService: GetCurrentUserDynamicMenusService,
        private readonly _router: Router,
        private readonly _route: ActivatedRoute,
        private readonly _getDashboardTileCountsService: GetDashboardTileCountsService,
        private readonly _currentEnvironmentService: CurrentEnvironmentService,
        private readonly _currentCaseService: CurrentCaseService,
        private readonly _changeDetectorWrapper: ChangeDetectorWrapper,
        private _titleService: Title
    ) { }

    public ngOnInit(): void {
        this._getDashboardsSubscription = this._getCurrentUserDynamicMenusService.getCurrentUserDashboards().subscribe(result => {
            this.tiles = [];
            const dashboardName = this._route.snapshot.params["dashboardName"] as string | undefined;

            if (dashboardName) {
                const dashboard = result.filter(x => x.name == dashboardName)[0];
                this._titleService.setTitle(`${this._titleService.getTitle()} (${dashboardName})`);
                
                if (dashboard && dashboard.dashboardTiles && dashboard.dashboardTiles.length) {
                    
                    if (dashboard.oeSubheader) {
                        this.oeSubheader = new ExtendedOESubheaderViewModel(dashboard.oeSubheader);
                    }

                    for (let dashboardTile of dashboard.dashboardTiles) {
                        const model = <ExtendedDashboardTileViewModel>{
                            dashboardTileId: dashboardTile.dashboardTileId,
                            dashboardTileName: dashboardTile.dashboardTileName,
                            dashboardTileTypeId: dashboardTile.dashboardTileTypeId,
                            lastUpdate: dashboardTile.lastUpdate
                        };

                        this.tiles.push(model);

                        this._getDashboardTileCountsService.getDashboardTileCounts(
                            {
                                dashboardTileId: model.dashboardTileId
                            },
                            {
                                concurrentRequestHandling: ConcurrentRequestHandlingEnum.AllowAll,
                                useLoadingIndicator: false
                            })
                            .subscribe(result => {
                                model.summary = result;
                                this._changeDetectorWrapper.detectChanges();
                            });
                    }
                    this._changeDetectorWrapper.detectChanges();
                } else {
                    this.navigateToDefaultDashboard(result);
                }
            } else {
                this.navigateToDefaultDashboard(result);
            }
        });
    }

    public setCurrentDetailTile(showing: boolean, tile: ExtendedDashboardTileViewModel): void {
        this.currentDetailTile = showing ? tile : undefined;
    }

    private navigateToDefaultDashboard(dashboards: DashboardViewModel[]): void {
        if (dashboards && dashboards.length) {
            this._router.navigate([this.getUrlPrefix() + 'Dashboards/' + dashboards[0].name]);
        } else {
            this._router.navigate([this.getUrlPrefix() + 'Participant/Search']);
        }
    }

    private getUrlPrefix(): string {
        return this._currentEnvironmentService.getCurrentEnvironmentName()
            + '/'
            + (
                this._currentCaseService.getCurrentCaseName()
                    ? this._currentCaseService.getCurrentCaseName() + '/'
                    : ''
            );
    }

    public ngOnDestroy(): void {
        this._getDashboardsSubscription && this._getDashboardsSubscription.unsubscribe();
    }
}

export class ExtendedOESubheaderViewModel {
    constructor(model: OESubheaderViewModel) {
        this.beginDate = moment.utc(model.beginDate).local().toDate();
        this.endDate = moment.utc(model.endDate).local().toDate();
        this.daysRemaining = Math.max(Math.floor((new Date(this.endDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)), 0);
    }

    public beginDate: Date;
    public endDate: Date;
    public daysRemaining: number;
}
